import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LandingService } from './service/landing.service';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { GenericService } from '../generic.service';
import { Subscription } from 'rxjs';
import { ApiComponentServices } from '../services/api-services';
import { defaultLanguage } from '../shared/constants/language.constants';
import { CrowdinService } from '../shared/service/crowdin.service';

declare global {
  interface Window {
    MSStream?: any;
  }
}
@Component({
    selector: 'app-landing-page',
    imports: [CommonModule, RouterModule],
    templateUrl: './landing-page.component.html',
    styleUrls: ['./landing-page.component.scss']
})
export class LandingPageComponent implements OnInit, OnDestroy {
  qrDetails: any;
  disMachineDetail: any;
  isLoading = true;
  qrId: any;
  deviceData: any = {};
  language: string = defaultLanguage;
  deviceData$: Subscription | null = null;
  selectedMachineType: string | null = null;
  machineImageUrl: string = '';
  routeId: string = '';
  accountNumber: string = '';
  accountKey: string = '';
  devices: any[] = [];
  selectedDevice: any = null;
  selectedMachineName: string | null = null;
  translations: { [key: string]: string } = {};
  currentLanguage: string = defaultLanguage;
  languageOptions: Array<{ value: string }> = [];
  isLoadingTranslations = true;
  subs = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private landingService: LandingService,
    private router: Router,
    private genericService: GenericService,
    private apiService: ApiComponentServices,
    private crowdinService: CrowdinService,
  ) {
    this.deviceData$ = this.genericService.deviceData.subscribe((data) => {
      if (data) {
        this.deviceData = data;
      }
    });
  }

  ngOnInit() {
    this.languageOptions = this.landingService.getLanguageOptions();

    const stored = localStorage.getItem('deviceData');
    const selectedName = stored
      ? JSON.parse(stored).languageSelected || defaultLanguage
      : defaultLanguage;
    this.currentLanguage = selectedName;

    this.subs.add(
      this.landingService.languageName$.subscribe((langName) => {
        this.currentLanguage = langName;
        this.translations = this.crowdinService.getAllTranslations();
      }),
    );

    this.landingService.loadTranslations(this.currentLanguage);

    this.route.paramMap.subscribe((params) => {
      const machineTypeParam = params.get('machineType');
      const machineType: number | null = machineTypeParam
        ? parseInt(machineTypeParam, 10)
        : null;
      this.routeId = params.get('id') || '';
      this.accountNumber = params.get('accountNumber') || '';
      if (machineType !== null) {
        this.deviceData.machineType = machineType;
        this.loadMachineImage(machineType);
      }
      if (this.accountNumber) {
        this.loadAccountDetailsDirectly();
      } else {
        this.isLoading = false;
      }
    });

    const currentSelectedType = this.genericService.getSelectedMachineType();
    this.getMachineName(currentSelectedType);
    if (
      !currentSelectedType &&
      this.deviceData?.devices &&
      this.deviceData.devices.length > 0
    ) {
      const firstDevice = this.deviceData.devices[0].MachineName;
      this.genericService.setSelectedMachineType(firstDevice);
    }
  }

  async languageChange(event: any) {
    const selectedLang = event.target.value;
    await this.landingService.loadTranslations(selectedLang);
  }

  loadAccountDetailsDirectly() {
    this.apiService
      .getAccountData(this.accountNumber, '', this.accountKey)
      .subscribe(
        (res) => {
          if (res) {
            this.disMachineDetail = res;
            this.devices = res.Devices || [];
            this.deviceData.accountNumber = res.AccountNumber;
            this.deviceData.businessName = res.BusinessName;
            this.deviceData.address = res.Address;
            this.deviceData.city = res.City;
            this.deviceData.state = res.State;
            this.deviceData.zip = res.ZipCode;
            this.deviceData.devices = this.devices;
            this.deviceData.languageSelected =
              res?.languageSelected || defaultLanguage;
            this.deviceData.accountName = res.AccountName;
            this.deviceData.countryCode = res.CountryIso3Code;

            this.genericService.setLocalStorage(this.deviceData);

            let selectedDevice = this.devices.find(
              (d) =>
                d.MachineName ===
                this.genericService.getLocalStorage().selectedMachineType,
            );

            if (!selectedDevice && this.devices.length > 0) {
              selectedDevice = this.devices[0];
            }

            if (selectedDevice) {
              this.setSelectedDevice(selectedDevice);
            }
          } else {
            this.disMachineDetail = null;
            this.devices = [];
          }
          this.isLoading = false;
        },
        (error) => {
          this.disMachineDetail = null;
          this.devices = [];
          this.isLoading = false;
        },
      );
  }

  setSelectedDevice(device: any) {
    this.selectedDevice = device;
    this.genericService.setLocalStorage({
      selectedMachineType: device.MachineName,
    });
    if (device.MachineType !== null && device.MachineType !== undefined) {
      this.loadMachineImage(device.MachineType);
    }
  }

  loadMachineImage(machineType: number) {
    this.landingService
      .getDeviceImageByModel(machineType)
      .subscribe((imgUrl) => {
        this.machineImageUrl = imgUrl;
      });
  }

  getMachineName(machineType: any) {
    this.selectedMachineName = machineType;
  }

  selectDevice(device: any) {
    this.selectedDevice = device;
  }

  checkQRMappedAndProceed() {
    if (!this.routeId) return;

    this.apiService.getQRMappedInfo(this.routeId).subscribe(
      (res) => {
        if (
          res &&
          res.AccountNumber === this.accountNumber &&
          res.IsQRVerified
        ) {
          this.deviceData = res;
          this.router.navigate([
            this.routeId,
            this.accountNumber,
            'troubleshoot',
          ]);
        } else {
          this.loadAccountDetailsDirectly();
        }
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
      },
    );
  }

  getQrDetails(qrId: string): void {
    this.landingService.getQrDetails(qrId).subscribe(
      (accountResponse) => {
        const accountNumber = accountResponse?.accountNumber;
        const accountKey = accountResponse?.accountKey;
        if (accountNumber) {
          this.apiService
            .getAccountData(accountNumber, '', accountKey)
            .subscribe(
              (accountData) => {
                this.qrDetails = accountData;
                if (this.qrDetails) {
                  this.getDishMachineDetails(this.qrDetails?.modelNumber);
                } else {
                  this.isLoading = false;
                }
              },
              (error) => {
                this.isLoading = false;
              },
            );
        } else {
          this.isLoading = false;
        }
      },
      (error) => {
        this.isLoading = false;
      },
    );
  }

  getDishMachineDetails(modelNumber: string): void {
    const id = parseInt(this.route.snapshot.paramMap.get('id') || '0', 10);
    this.landingService.getDishMachineDetails(id).subscribe((disMachine) => {
      this.disMachineDetail = disMachine[modelNumber];
      this.isLoading = false;
    });
  }

  navigate(pageName: string) {
    const accountNumber = this.accountNumber || '';

    if (!accountNumber || accountNumber.trim() === '') {
      return;
    }

    if (pageName === 'dishmachineModel') {
      this.router.navigate(['/dishmachineModel', accountNumber]);
    } else if (pageName === 'training') {
      this.router.navigate(['/training']);
    } else if (pageName === 'troubleShoot') {
      const selectedMachineType =
        this.genericService.getLocalStorage().selectedMachineType ||
        this.selectedMachineType;
      if (!selectedMachineType) return;

      this.router.navigate(['/troubleShootDesc', accountNumber], {
        queryParams: { machineType: selectedMachineType },
      });
    } else {
      this.router.navigate(['/troubleShootDesc', accountNumber]);
    }
  }

  setDeviceData() {
    this.deviceData = {
      modelNumber: this.qrDetails.modelNumber,
      machineImage: this.disMachineDetail.machineImage,
      model: this.disMachineDetail.model,
      productLine: this.disMachineDetail.productLine,
      issueName: null,
      languageSelected: this.currentLanguage,
    };
    this.genericService.setLocalStorage(this.deviceData);
    this.genericService.deviceData.next(this.deviceData);
  }

  getDeviceType(): 'ios' | 'android' | 'mac' | 'windows' | 'unknown' {
    const userAgent = navigator.userAgent || navigator.vendor;

    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'ios';
    } else if (/Android/.test(userAgent)) {
      return 'android';
    } else if (
      /Macintosh/.test(userAgent) &&
      !/iPhone|iPad|iPod/.test(userAgent)
    ) {
      return 'mac';
    } else if (/Windows NT/.test(userAgent)) {
      return 'windows';
    }
    return 'unknown';
  }

  download() {
    const deviceType = this.getDeviceType();

    let url = '';
    if (deviceType === 'ios' || deviceType === 'mac') {
      url = 'https://apps.apple.com/us/app/dishiq/id6698876936';
    } else if (deviceType === 'android' || deviceType === 'windows') {
      url =
        'https://play.google.com/store/apps/details?id=com.ecolab.apps.aidishmachineapp';
    }

    window.location.href = url;
  }

  ngOnDestroy() {
    if (this.deviceData$) {
      this.deviceData$.unsubscribe();
    }
    this.deviceData['languageSelected'] = this.currentLanguage;
    this.genericService.setLocalStorage(this.deviceData);
    this.genericService.deviceData.next(this.deviceData);
  }
}
